/**
 * B2B Partnerships Domain Seed Data
 *
 * Contains all seed data for CRM Service (34) B2B features:
 * - Referral Agreements (bilateral partnership contracts)
 * - Agreement Commission Structures
 * - B2B Commissions (earned commissions)
 *
 * @see PR #246 - Partner Companies B2B Model
 */

import { seedId } from '../utils';
import { BUSINESS_IDS } from './tenants';
import { USER_IDS } from './users';

// =============================================================================
// REFERRAL AGREEMENT IDs
// =============================================================================

export const REFERRAL_AGREEMENT_IDS = {
  // Active agreements
  APEX_TO_FUNDING: seedId('referral-agreement-apex-funding', 1),
  PACIFIC_TO_FUNDING: seedId('referral-agreement-pacific-funding', 1),
  FUNDING_TO_SHARPE: seedId('referral-agreement-funding-sharpe', 1),

  // Two-way agreement
  FUNDING_SHARPE_BILATERAL: seedId('referral-agreement-bilateral', 1),

  // Draft/Pending
  GULFCOAST_TO_FUNDING_DRAFT: seedId('referral-agreement-gulf-draft', 1),
} as const;

export type ReferralAgreementId = (typeof REFERRAL_AGREEMENT_IDS)[keyof typeof REFERRAL_AGREEMENT_IDS];

// =============================================================================
// COMMISSION STRUCTURE IDs
// =============================================================================

export const COMMISSION_STRUCTURE_IDS = {
  APEX_FUNDING_DEFAULT: seedId('comm-structure-apex-default', 1),
  APEX_FUNDING_PREMIUM: seedId('comm-structure-apex-premium', 2),
  PACIFIC_FUNDING_FLAT: seedId('comm-structure-pacific-flat', 1),
  FUNDING_SHARPE_TIERED: seedId('comm-structure-funding-tiered', 1),
} as const;

export type CommissionStructureId = (typeof COMMISSION_STRUCTURE_IDS)[keyof typeof COMMISSION_STRUCTURE_IDS];

// =============================================================================
// B2B COMMISSION IDs
// =============================================================================

export const B2B_COMMISSION_IDS = {
  // Paid commissions
  APEX_COMM_PAID_1: seedId('b2b-commission-apex-paid', 1),
  APEX_COMM_PAID_2: seedId('b2b-commission-apex-paid', 2),

  // Pending commissions
  APEX_COMM_PENDING_1: seedId('b2b-commission-apex-pending', 1),
  PACIFIC_COMM_PENDING_1: seedId('b2b-commission-pacific-pending', 1),

  // Disputed commission
  SHARPE_COMM_DISPUTED: seedId('b2b-commission-sharpe-disputed', 1),
} as const;

export type B2BCommissionId = (typeof B2B_COMMISSION_IDS)[keyof typeof B2B_COMMISSION_IDS];

// =============================================================================
// DATA TYPES
// =============================================================================

export interface CommissionStructureSeedData {
  id: string;
  calculationMethod: 'flat_amount' | 'percentage_loan' | 'percentage_revenue' | 'tiered_volume' | 'tiered_count';
  calculationBase?: 'loan_amount' | 'revenue' | 'origination_fee' | 'total_fees';
  rate?: number; // Decimal for percentage (0.005 = 0.5%)
  flatAmount?: number;
  tierType?: 'monthly_volume' | 'quarterly_volume' | 'annual_volume' | 'deal_count';
  tiers?: { min: number; max: number; rate: number }[];
  applicableProducts: string[];
  applicableStates: string[];
  priority: number;
  isDefault: boolean;
}

export interface ReferralAgreementSeedData {
  id: string;
  businessId: string; // Receiving business (lender)
  sendingBusinessId: string; // Partner sending referrals
  receivingBusinessId: string; // Business receiving referrals
  agreementType: 'one_way' | 'two_way';
  status: 'draft' | 'pending_approval' | 'active' | 'suspended' | 'terminated' | 'expired';
  effectiveDate?: string;
  expirationDate?: string;
  exclusivityScope?: 'none' | 'geographic' | 'product_type' | 'full';
  exclusiveProducts?: string[];
  exclusiveStates?: string[];
  commissionTrigger: 'submission' | 'approval' | 'funding' | 'first_payment';
  paymentTerms: 'immediate' | 'net_15' | 'net_30' | 'net_45' | 'net_60';
  documentKey?: string;
  documentName?: string;
  commissionStructures: CommissionStructureSeedData[];
  metadata?: Record<string, unknown>;
}

export interface B2BCommissionSeedData {
  id: string;
  businessId: string;
  agreementId: string;
  structureId: string;
  caseId?: string;
  loanNumber?: string;
  sendingBusinessId: string;
  receivingBusinessId: string;
  referringUserId?: string;
  loanAmount: number;
  commissionAmount: number;
  calculatedRate: number;
  calculationMethod: string;
  status: 'pending' | 'approved' | 'processing' | 'paid' | 'cancelled' | 'disputed';
  approvedAt?: string;
  approvedBy?: string;
  paymentDueDate?: string;
  paidAt?: string;
  paymentMethod?: 'wire' | 'ach' | 'check';
  paymentReference?: string;
  receiptStatus?: 'pending' | 'acknowledged' | 'disputed' | 'resolved';
  receiptDisputeReason?: string;
  notes?: string;
}

// =============================================================================
// REFERRAL AGREEMENT DATA
// =============================================================================

export const REFERRAL_AGREEMENTS: Record<keyof typeof REFERRAL_AGREEMENT_IDS, ReferralAgreementSeedData> = {
  APEX_TO_FUNDING: {
    id: REFERRAL_AGREEMENT_IDS.APEX_TO_FUNDING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    sendingBusinessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL, // Using as "Apex" proxy
    receivingBusinessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    agreementType: 'one_way',
    status: 'active',
    effectiveDate: '2025-01-01',
    expirationDate: '2026-12-31',
    exclusivityScope: 'none',
    commissionTrigger: 'funding',
    paymentTerms: 'net_30',
    documentKey: 'agreements/apex-funding-2025.pdf',
    documentName: 'Apex-Funding Referral Agreement 2025',
    commissionStructures: [
      {
        id: COMMISSION_STRUCTURE_IDS.APEX_FUNDING_DEFAULT,
        calculationMethod: 'percentage_loan',
        calculationBase: 'loan_amount',
        rate: 0.005, // 0.5%
        applicableProducts: ['fix_and_flip', 'bridge', 'rental'],
        applicableStates: [],
        priority: 1,
        isDefault: true,
      },
      {
        id: COMMISSION_STRUCTURE_IDS.APEX_FUNDING_PREMIUM,
        calculationMethod: 'percentage_loan',
        calculationBase: 'loan_amount',
        rate: 0.0075, // 0.75% for large loans
        applicableProducts: ['fix_and_flip', 'bridge'],
        applicableStates: ['TX', 'FL', 'CA'],
        priority: 2,
        isDefault: false,
      },
    ],
    metadata: { signedBy: 'Rachel Martinez', partnerContact: 'partner@apex.example' },
  },
  PACIFIC_TO_FUNDING: {
    id: REFERRAL_AGREEMENT_IDS.PACIFIC_TO_FUNDING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    sendingBusinessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    receivingBusinessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    agreementType: 'one_way',
    status: 'active',
    effectiveDate: '2025-06-01',
    commissionTrigger: 'funding',
    paymentTerms: 'net_15',
    commissionStructures: [
      {
        id: COMMISSION_STRUCTURE_IDS.PACIFIC_FUNDING_FLAT,
        calculationMethod: 'flat_amount',
        flatAmount: 2500, // Flat $2,500 per funded deal
        applicableProducts: [],
        applicableStates: [],
        priority: 1,
        isDefault: true,
      },
    ],
  },
  FUNDING_TO_SHARPE: {
    id: REFERRAL_AGREEMENT_IDS.FUNDING_TO_SHARPE,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    sendingBusinessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    receivingBusinessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    agreementType: 'one_way',
    status: 'active',
    effectiveDate: '2025-03-01',
    commissionTrigger: 'funding',
    paymentTerms: 'net_30',
    commissionStructures: [
      {
        id: COMMISSION_STRUCTURE_IDS.FUNDING_SHARPE_TIERED,
        calculationMethod: 'tiered_volume',
        tierType: 'monthly_volume',
        tiers: [
          { min: 0, max: 1000000, rate: 0.004 },
          { min: 1000001, max: 5000000, rate: 0.005 },
          { min: 5000001, max: 50000000, rate: 0.006 }, // 50M max to avoid numeric overflow
        ],
        applicableProducts: ['construction', 'ground_up'],
        applicableStates: [],
        priority: 1,
        isDefault: true,
      },
    ],
  },
  FUNDING_SHARPE_BILATERAL: {
    id: REFERRAL_AGREEMENT_IDS.FUNDING_SHARPE_BILATERAL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    sendingBusinessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    receivingBusinessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    agreementType: 'two_way',
    status: 'active',
    effectiveDate: '2025-01-01',
    expirationDate: '2027-12-31',
    exclusivityScope: 'product_type',
    exclusiveProducts: ['construction'],
    commissionTrigger: 'funding',
    paymentTerms: 'net_30',
    commissionStructures: [],
  },
  GULFCOAST_TO_FUNDING_DRAFT: {
    id: REFERRAL_AGREEMENT_IDS.GULFCOAST_TO_FUNDING_DRAFT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    sendingBusinessId: BUSINESS_IDS.GULF_COAST_FO,
    receivingBusinessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    agreementType: 'one_way',
    status: 'draft',
    commissionTrigger: 'funding',
    paymentTerms: 'net_45',
    commissionStructures: [],
    metadata: { draftCreatedBy: 'Rachel Martinez', pendingReview: true },
  },
};

// =============================================================================
// B2B COMMISSION DATA
// =============================================================================

export const B2B_COMMISSIONS: Record<keyof typeof B2B_COMMISSION_IDS, B2BCommissionSeedData> = {
  APEX_COMM_PAID_1: {
    id: B2B_COMMISSION_IDS.APEX_COMM_PAID_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    agreementId: REFERRAL_AGREEMENT_IDS.APEX_TO_FUNDING,
    structureId: COMMISSION_STRUCTURE_IDS.APEX_FUNDING_DEFAULT,
    loanNumber: 'LC-2025-0423',
    sendingBusinessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    receivingBusinessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    referringUserId: USER_IDS.RACHEL_MARTINEZ,
    loanAmount: 450000,
    commissionAmount: 2250, // 0.5% of 450k
    calculatedRate: 0.005,
    calculationMethod: 'percentage_loan',
    status: 'paid',
    approvedAt: '2025-11-15T10:00:00Z',
    approvedBy: USER_IDS.RACHEL_MARTINEZ,
    paymentDueDate: '2025-12-15',
    paidAt: '2025-12-10T14:30:00Z',
    paymentMethod: 'ach',
    paymentReference: 'ACH-2025-12-001',
    receiptStatus: 'acknowledged',
    notes: 'Q4 2025 referral - Marcus Thompson property',
  },
  APEX_COMM_PAID_2: {
    id: B2B_COMMISSION_IDS.APEX_COMM_PAID_2,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    agreementId: REFERRAL_AGREEMENT_IDS.APEX_TO_FUNDING,
    structureId: COMMISSION_STRUCTURE_IDS.APEX_FUNDING_PREMIUM,
    loanNumber: 'LC-2025-0456',
    sendingBusinessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    receivingBusinessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    loanAmount: 1250000,
    commissionAmount: 9375, // 0.75% premium rate
    calculatedRate: 0.0075,
    calculationMethod: 'percentage_loan',
    status: 'paid',
    approvedAt: '2025-12-01T09:00:00Z',
    paidAt: '2025-12-28T11:00:00Z',
    paymentMethod: 'wire',
    paymentReference: 'WIRE-2025-12-003',
    receiptStatus: 'acknowledged',
  },
  APEX_COMM_PENDING_1: {
    id: B2B_COMMISSION_IDS.APEX_COMM_PENDING_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    agreementId: REFERRAL_AGREEMENT_IDS.APEX_TO_FUNDING,
    structureId: COMMISSION_STRUCTURE_IDS.APEX_FUNDING_DEFAULT,
    loanNumber: 'LC-2026-0012',
    sendingBusinessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    receivingBusinessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    loanAmount: 380000,
    commissionAmount: 1900,
    calculatedRate: 0.005,
    calculationMethod: 'percentage_loan',
    status: 'pending',
    paymentDueDate: '2026-02-15',
    notes: 'January 2026 funding - pending approval',
  },
  PACIFIC_COMM_PENDING_1: {
    id: B2B_COMMISSION_IDS.PACIFIC_COMM_PENDING_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    agreementId: REFERRAL_AGREEMENT_IDS.PACIFIC_TO_FUNDING,
    structureId: COMMISSION_STRUCTURE_IDS.PACIFIC_FUNDING_FLAT,
    loanNumber: 'LC-2026-0008',
    sendingBusinessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    receivingBusinessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    loanAmount: 520000,
    commissionAmount: 2500, // Flat fee
    calculatedRate: 0,
    calculationMethod: 'flat_amount',
    status: 'approved',
    approvedAt: '2026-01-10T15:00:00Z',
    paymentDueDate: '2026-01-25',
    receiptStatus: 'pending',
  },
  SHARPE_COMM_DISPUTED: {
    id: B2B_COMMISSION_IDS.SHARPE_COMM_DISPUTED,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    agreementId: REFERRAL_AGREEMENT_IDS.FUNDING_TO_SHARPE,
    structureId: COMMISSION_STRUCTURE_IDS.FUNDING_SHARPE_TIERED,
    loanNumber: '1SC-2025-0892',
    sendingBusinessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    receivingBusinessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    loanAmount: 2100000,
    commissionAmount: 10500,
    calculatedRate: 0.005,
    calculationMethod: 'tiered_volume',
    status: 'disputed',
    approvedAt: '2025-12-20T10:00:00Z',
    receiptStatus: 'disputed',
    receiptDisputeReason: 'Loan amount reported incorrectly - actual funded amount was $2.3M',
    notes: 'Dispute raised by 1Sharpe - under review',
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getAllReferralAgreements(workspace?: string): ReferralAgreementSeedData[] {
  const agreements = Object.values(REFERRAL_AGREEMENTS);
  if (!workspace) return agreements;
  return agreements.map((a) => ({
    ...a,
    documentName: a.documentName ? `${a.documentName} [${workspace}]` : undefined,
  }));
}

export function getAllB2BCommissions(): B2BCommissionSeedData[] {
  return Object.values(B2B_COMMISSIONS);
}

export function getAgreementsByBusiness(businessId: string): ReferralAgreementSeedData[] {
  return Object.values(REFERRAL_AGREEMENTS).filter(
    (a) => a.businessId === businessId || a.sendingBusinessId === businessId || a.receivingBusinessId === businessId,
  );
}

export function getCommissionsByAgreement(agreementId: string): B2BCommissionSeedData[] {
  return Object.values(B2B_COMMISSIONS).filter((c) => c.agreementId === agreementId);
}

export function getB2BCommissionsByStatus(status: B2BCommissionSeedData['status']): B2BCommissionSeedData[] {
  return Object.values(B2B_COMMISSIONS).filter((c) => c.status === status);
}

export function getActiveAgreements(): ReferralAgreementSeedData[] {
  return Object.values(REFERRAL_AGREEMENTS).filter((a) => a.status === 'active');
}

// =============================================================================
// TOP-LEVEL COMMISSION STRUCTURE DATA (For API Seeder)
// =============================================================================

/**
 * Top-level commission structure for API seeding (different from embedded structure)
 */
export interface TopLevelCommissionStructureSeedData {
  id: string;
  businessId: string;
  name: string;
  description?: string;
  structureType: 'flat' | 'percentage' | 'tiered' | 'hybrid';
  baseRate?: number;
  flatAmount?: number; // For flat fee structures (dollar amount)
  tiers?: { min: number; max: number; rate: number }[];
  bonusRules?: { condition: string; bonus: number }[];
  productMultipliers?: { productType: string; multiplier: number }[];
  isDefault: boolean;
  isActive: boolean;
}

export const COMMISSION_STRUCTURES: Record<keyof typeof COMMISSION_STRUCTURE_IDS, TopLevelCommissionStructureSeedData> =
  {
    APEX_FUNDING_DEFAULT: {
      id: COMMISSION_STRUCTURE_IDS.APEX_FUNDING_DEFAULT,
      businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
      name: 'Apex Standard Referral Rate',
      description: 'Standard 0.5% commission on all referred loans from Apex partners',
      structureType: 'percentage',
      baseRate: 0.005,
      isDefault: true,
      isActive: true,
    },
    APEX_FUNDING_PREMIUM: {
      id: COMMISSION_STRUCTURE_IDS.APEX_FUNDING_PREMIUM,
      businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
      name: 'Apex Premium Rate',
      description: 'Premium 0.75% rate for large loans in key markets (TX, FL, CA)',
      structureType: 'percentage',
      baseRate: 0.0075,
      productMultipliers: [
        { productType: 'fix_and_flip', multiplier: 1.0 },
        { productType: 'bridge', multiplier: 1.0 },
      ],
      isDefault: false,
      isActive: true,
    },
    PACIFIC_FUNDING_FLAT: {
      id: COMMISSION_STRUCTURE_IDS.PACIFIC_FUNDING_FLAT,
      businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
      name: 'Pacific Flat Fee',
      description: 'Flat $2,500 fee per funded deal from Pacific Bridge partners',
      structureType: 'flat',
      baseRate: 0, // No percentage rate for flat structures
      flatAmount: 2500, // $2,500 flat fee per funded deal
      isDefault: true,
      isActive: true,
    },
    FUNDING_SHARPE_TIERED: {
      id: COMMISSION_STRUCTURE_IDS.FUNDING_SHARPE_TIERED,
      businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
      name: '1Sharpe Tiered Volume',
      description: 'Tiered commission based on monthly referral volume',
      structureType: 'tiered',
      tiers: [
        { min: 0, max: 1000000, rate: 0.004 },
        { min: 1000001, max: 5000000, rate: 0.005 },
        { min: 5000001, max: 50000000, rate: 0.006 }, // 50M max to avoid numeric overflow
      ],
      isDefault: true,
      isActive: true,
    },
  };

export function getAllCommissionStructures(workspace?: string): TopLevelCommissionStructureSeedData[] {
  const structures = Object.values(COMMISSION_STRUCTURES);
  if (!workspace) return structures;
  return structures.map((s) => ({
    ...s,
    name: `${s.name} [${workspace}]`,
  }));
}
